import type { TimezoneSort } from "@p81-common/ui/v2/timezone-picker";

import { weekDaysOptions } from "@p81-common/shell-core-js";
import { links } from "@p81-common/shell-core-js";
import type { RelativeUnit } from "./dateRangeStatus";

export { MAX_SELECTABLE_DATE } from "./dateRangeStatus";

// Figma uses a dedicated clock illustration for the expired state; that exact
// asset isn't in the repo, so we reuse the existing alert illustration.
export const expiredIllustrationSrc = `${links.imagesBaseUrl}/png/illus_OrgSec_alert.png`;

export const DRAWER_WIDTH = 611;
export const DEFAULT_START_HOUR = 9; // 09:00
export const DEFAULT_END_HOUR = 17; // 17:00
// End of day is hour 0 (00:00), serialized to the wire as { hour: 0, minute: 0 }.
export const END_OF_DAY_HOUR = 0;

// The backend validates the timezone against the IANA list derived from
// Intl.supportedValuesOf('timeZone'), plus the two literals "UTC" and "Local".
// We must send a value from exactly that list (exact casing) or it 422s.
// "Local" = device local clock, no conversion. There is no empty/omitted case
// anymore — absence on the wire would be read as UTC, so we always send a value.
export const LOCAL_TZ = "Local";
export const UTC_TZ = "UTC";

// The v2 TimezonePicker builds its options from this IANA list (same source the
// backend validates against: Intl.supportedValuesOf). We prepend the two
// literals the backend also accepts — "Local" (device clock) and "UTC" — so the
// picker can select them. Module-level constant: a stable reference, as the
// component requires (a new array each render rebuilds the option list).
const getIanaZones = (): string[] => {
  const intl = Intl as typeof Intl & {
    supportedValuesOf?: (key: string) => string[];
  };
  try {
    return intl.supportedValuesOf ? intl.supportedValuesOf("timeZone") : [];
  } catch {
    return [];
  }
};

export const TIMEZONE_ZONES: string[] = [
  LOCAL_TZ,
  UTC_TZ,
  ...getIanaZones().filter((zone) => zone !== UTC_TZ),
];

// Pin "Local" then "UTC" at the top; everything else sorted alphabetically by
// zone name.
export const sortTimezones: TimezoneSort = (a, b) => {
  const rank = (value: string) =>
    value === LOCAL_TZ ? -2 : value === UTC_TZ ? -1 : 0;
  const diff = rank(a.value) - rank(b.value);
  return diff !== 0 ? diff : a.value.localeCompare(b.value);
};

const pad2 = (n: number) => (n < 10 ? `0${n}` : `${n}`);

// Start: 00:00 → 23:00.
export const startHourOptions = Array.from({ length: 24 }, (_, hour) => ({
  value: String(hour),
  label: `${pad2(hour)}:00`,
}));

// End: 01:00 → 23:00, then "00:00 (End of day)" (hour 0 = end of day).
export const endHourOptions = [
  ...Array.from({ length: 23 }, (_, i) => {
    const hour = i + 1;
    return { value: String(hour), label: `${pad2(hour)}:00` };
  }),
  { value: String(END_OF_DAY_HOUR), label: "00:00 (End of day)" },
];

export const weekdayItems = weekDaysOptions.map(
  ({ value, label }: { value: string; label: string }) => ({
    value,
    label,
  })
);

export const relativeUnitOptions: { value: RelativeUnit; label: string }[] = [
  { value: "days", label: "Days" },
  { value: "weeks", label: "Weeks" },
  { value: "months", label: "Months" },
];
